import { useEffect, useState } from 'react';
import { useGetApiData } from '@grc/ui-utility';
import { useAddressSearch } from '@grc/ui-shared';
import { BusinessEntityType } from '@grc/ui-shared/lib/components/models/enums/enums';
import { TransportStatus } from '../../../enums/loads.enum';
import { GetShipments } from '../../../service/api/get-shipments-service';
import getOrderDetails from '../../../service/api/get-order-details';
import { GetTransportById } from '../../../service/api/get-transport-by-id';
import { getTrackedTransportationDetailsById } from '../../../service/api/get-tracked-transportation-details-by-id';
import { fetchBusinessEntities } from '../../../service/api/fetch-business-entities';
import {
  fetchOrderById,
  fetchTrackedTransportsByIdUrl
} from '../../../common/constants/url-constants';
import {
  homeShipmentsAccountId,
  trackingService
} from '../../../common/constants/constants';
import { IShipment } from '../../../models/shipments/shipment.interface';
import { IDateRangeFilterValue } from '../../../common/date-range-filter/date-range-filter.interface';
import { toApiDateRange } from '../../../common/date-range-filter/date-range-filter.utils';
import { resolveShipmentTransportIds } from '../utils/resolve-shipment-transport-ids';

export type EtaStatus = 'onTime' | 'delay';

export interface IMapPosition {
  lat: number;
  lng: number;
}

export interface IShipmentHeatMapPin {
  shipment: IShipment;
  transportId?: string;
  carrierId?: string;
  transport?: any;
  position?: IMapPosition;
  hasLiveLocation: boolean;
  etaStatus: EtaStatus;
}

export function addressToString(address: any): string {
  if (!address) return '';
  const street = Array.isArray(address.streetAddress)
    ? address.streetAddress.join(' ')
    : address.streetAddress;
  return [street, address.city, address.provinceState, address.postalZipCode]
    .filter(Boolean)
    .join(', ');
}

// Best-effort Country enum -> ISO alpha-2 for the geocoding call. Only the
// three values this app's location data realistically uses are mapped;
// unmapped values fall back to 'US' (same default the deprecated
// useAzureMaps hook used for this same purpose).
function addressCountryCode(address: any): string {
  switch (address?.country) {
    case 1:
      return 'CA';
    case 3:
      return 'MX';
    default:
      return 'US';
  }
}

// Client-side approximation only - there is no ETA/on-time computation
// anywhere in the backend yet (see docs/shipment-tracking-map-api-gaps.md,
// gap #3). Flags "delay" once the delivery window has closed and the
// shipment hasn't been marked delivered; this is a stand-in until the
// backend exposes a real ETA-vs-route computation.
function resolveEtaStatus(shipment: IShipment): EtaStatus {
  if (shipment.status === TransportStatus.Delivered) return 'onTime';

  const closeDate = shipment.delivery?.closeDate
    ? new Date(shipment.delivery.closeDate)
    : undefined;
  if (!closeDate || Number.isNaN(closeDate.getTime())) return 'onTime';

  return closeDate.getTime() < Date.now() ? 'delay' : 'onTime';
}

// Shipment-centric heat map data, built entirely on top of existing
// endpoints (no shipment-keyed tracking endpoint exists yet - gap #1 in the
// doc above). For each in-transit shipment: resolve its transport the same
// way useTrackingUpdates.ts does (order -> matching legs), then read the
// last LocationUpdate's lat/long off that transport's tracked details. When
// there's no live location yet, fall back to the origin business entity's
// stored coordinates, or geocode its address as a last resort. This is an
// N+1 fan-out (order + transport + tracking fetch per shipment) - acceptable
// at the in-transit-only scale fetched here, but not something that should
// scale to "all shipments" without the bulk endpoint described in the gap
// doc.
export function useShipmentHeatMapData(
  searchText: string,
  dateFilter: IDateRangeFilterValue
) {
  const { fetchData } = useGetApiData();
  const { getCoordinatesByAddress } = useAddressSearch(trackingService);
  const [pins, setPins] = useState<IShipmentHeatMapPin[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const geocodeOrigin = async (
      shipment: IShipment
    ): Promise<IMapPosition | undefined> => {
      if (!shipment.originId) return undefined;

      const { map } = await fetchBusinessEntities({
        fetchData,
        entityIds: [shipment.originId],
        entityType: BusinessEntityType.Location
      });
      const origin: any = map?.[shipment.originId];

      if (origin?.address?.latitude != null && origin?.address?.longitude != null) {
        return { lat: origin.address.latitude, lng: origin.address.longitude };
      }

      const addressText = addressToString(origin?.address);
      if (!addressText) return undefined;

      const coords = await getCoordinatesByAddress(
        addressText,
        addressCountryCode(origin?.address)
      );
      return coords ? { lat: coords.latitude, lng: coords.longitude } : undefined;
    };

    const resolvePin = async (
      shipment: IShipment
    ): Promise<IShipmentHeatMapPin> => {
      const etaStatus = resolveEtaStatus(shipment);

      const order = shipment.orderId
        ? await getOrderDetails(shipment.orderId, fetchOrderById, fetchData)
        : null;
      const transportId = resolveShipmentTransportIds(
        order,
        shipment.shipmentNumber
      )[0];

      if (!transportId) {
        return {
          shipment,
          etaStatus,
          hasLiveLocation: false,
          position: await geocodeOrigin(shipment)
        };
      }

      const [transport, trackedTransport] = await Promise.all([
        GetTransportById(transportId, fetchData),
        getTrackedTransportationDetailsById(
          fetchTrackedTransportsByIdUrl.concat(`/${transportId}`),
          fetchData,
          trackingService
        )
      ]);

      const locationUpdates: any[] = (trackedTransport as any)?.locationUpdates ?? [];
      const lastUpdate = locationUpdates[locationUpdates.length - 1];
      const address = lastUpdate?.address;

      const position =
        address?.latitude != null && address?.longitude != null
          ? { lat: address.latitude, lng: address.longitude }
          : await geocodeOrigin(shipment);

      return {
        shipment,
        transportId,
        carrierId: transport?.carrier?.carrierId,
        transport,
        position,
        hasLiveLocation: !!(address?.latitude != null && address?.longitude != null),
        etaStatus
      };
    };

    const load = async () => {
      setIsLoading(true);

      const shipments = await GetShipments(fetchData, {
        accountId: homeShipmentsAccountId,
        status: 'inTransit',
        search: searchText || undefined,
        ...toApiDateRange(dateFilter),
        dateFilterType: dateFilter.dateFilterType
      });

      if (isCancelled) return;

      const resolved = await Promise.all(shipments.map(resolvePin));

      if (!isCancelled) {
        setPins(resolved);
        setIsLoading(false);
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [fetchData, searchText, dateFilter, getCoordinatesByAddress]);

  return { pins, isLoading };
}

export default useShipmentHeatMapData;
